import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { facebookPixel } from '../services/facebookPixel';

/**
 * Hook para rastreamento automático com Facebook Pixel
 * Rastreia automaticamente mudanças de rota
 */
export function usePixelTracking() {
  const location = useLocation();

  // Rastreia PageView em cada mudança de rota
  useEffect(() => {
    const getPageTitle = (): string => {
      const pathname = location.pathname;

      const titleMap: Record<string, string> = {
        '/': 'Home',
        '/search': 'Buscar Séries',
        '/watchlist': 'Minha Watchlist',
        '/profile': 'Meu Perfil',
        '/plans': 'Planos de Assinatura',
      };

      // Verifica rotas dinâmicas
      if (pathname.startsWith('/series/')) return 'Detalhes da Série';
      if (pathname.startsWith('/watch/')) return 'Assistindo';

      return titleMap[pathname] || 'TvDorama';
    };

    facebookPixel.trackPageView(getPageTitle(), location.pathname);
  }, [location]);

  return { facebookPixel };
}

/**
 * Hook para rastreamento de eventos de autenticação
 */
export function useAuthPixelTracking() {
  return {
    trackLogin: (email: string, status: 'success' | 'failed' = 'success') => {
      facebookPixel.trackCompleteRegistration(email, status);
    },
    trackRegister: (
      email: string,
      firstName?: string,
      lastName?: string
    ) => {
      facebookPixel.trackCompleteRegistration(email, 'success', firstName, lastName);
    },
    trackLoginError: (email: string) => {
      facebookPixel.trackCompleteRegistration(email, 'failed');
    },
  };
}

/**
 * Hook para rastreamento de eventos de série/conteúdo
 */
export function useContentPixelTracking() {
  return {
    trackViewSeries: (seriesName: string, seriesId: string) => {
      facebookPixel.trackViewContent(seriesName, [seriesId], 0, 'Series');
    },
    trackAddToWatchlist: (seriesName: string, seriesId: string) => {
      facebookPixel.trackAddToWishlist(seriesName, [seriesId]);
    },
    trackRemoveFromWatchlist: (seriesName: string, seriesId: string) => {
      facebookPixel.trackRemoveFromWishlist(seriesName, [seriesId]);
    },
    trackSearch: (searchQuery: string) => {
      facebookPixel.trackSearch(searchQuery);
    },
  };
}

/**
 * Hook para rastreamento de eventos de pagamento/planos
 */
export function usePaymentPixelTracking() {
  return {
    trackViewPlans: () => {
      facebookPixel.trackViewContent(
        'Planos de Assinatura',
        [],
        0,
        'Subscription'
      );
    },
    trackSelectPlan: (planName: string, price: number, planId: string) => {
      facebookPixel.trackInitiateCheckout(planName, price, planId, 1);
    },
    trackPixPaymentAttempt: (planName: string, price: number) => {
      facebookPixel.trackAddPaymentInfo('pix', planName, price);
    },
    trackPixGenerated: (
      planName: string,
      orderId: string,
      expirationTime: string,
      price: number
    ) => {
      facebookPixel.trackPixGenerated(planName, orderId, expirationTime, price);
    },
    trackPurchaseComplete: (
      planName: string,
      orderId: string,
      price: number,
      planId: string
    ) => {
      facebookPixel.trackPurchase(planName, orderId, price, planId, 'pix');
    },
  };
}

/**
 * Hook para rastreamento de eventos de vídeo
 */
export function useVideoPixelTracking() {
  return {
    trackPlay: (videoName: string, durationSeconds: number) => {
      facebookPixel.trackVideoPlay(videoName, durationSeconds);
    },
    trackComplete: (videoName: string) => {
      facebookPixel.trackVideoComplete(videoName);
    },
    trackAttemptPlayPremium: (seriesName: string, price: number, seriesId: string) => {
      facebookPixel.trackInitiateCheckout(seriesName, price, seriesId);
    },
  };
}

/**
 * Hook para rastreamento de erros
 */
export function useErrorPixelTracking() {
  return {
    trackError: (errorType: string, message: string, page: string) => {
      facebookPixel.trackError(errorType, message, page);
    },
  };
}

export { facebookPixel };
